import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {Box, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Topbar from './Topbar';
import AccuracyBox from './analysis/AccuracyBox';
import { HORIZON_KEYS, pctLabel } from './analysis/accuracyApi';
import { toAccuracyNode, toAccuracyByLine } from './analysis/accuracyFromFirestore';
import AccuracyChart from './analysis/AccuracyChart';
import { useHomeLive, ACCURACY_STALE_AFTER_MS } from './home/useHomeLive';
import { useAccuracyHistory } from './home/useAccuracyHistory';

const BUILD_VERSION = process.env.REACT_APP_VERSION ?? 'dev';
const BUILD_DATE = process.env.REACT_APP_BUILD_DATE ?? '';

type AnalysisCard = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  route: string;
  available: boolean;
  icon: string;
  stat?: string;
  statLabel?: string;
};

const CARDS: AnalysisCard[] = [
  {
    id: 'messages',
    title: 'Fehlende Meldungen',
    subtitle: 'Missing Messages',
    description: 'Identify gaps in forecast message coverage across routes and stations. Detect which departures are failing to generate occupancy predictions.',
    route: '/analysis/messages',
    available: true,
    icon: '⚠',
    stat: '—',
    statLabel: 'gaps detected',
  },
  {
    id: 'coverage',
    title: 'Streckenabdeckung',
    subtitle: 'Route Coverage',
    description: 'Visualise which routes and stop sequences have consistent message coverage versus sporadic or absent reporting.',
    route: '/analysis/coverage',
    available: true,
    icon: '◈',
    stat: '—',
    statLabel: 'routes monitored',
  },
  {
    id: 'delays',
    title: 'Verspätungsmuster',
    subtitle: 'Delay Patterns',
    description: 'Explore delay distributions across lines, times of day, and station sequences. Surface systemic patterns in forecast accuracy.',
    route: '/analysis/delays',
    available: false,
    icon: '◷',
    stat: '—',
    statLabel: 'avg delay (min)',
  },
  {
    id: 'occupancy',
    title: 'Auslastungstrends',
    subtitle: 'Occupancy Trends',
    description: 'Analyse passenger load trends over time by line and carriage group. Compare live observations against forecast prognoses.',
    route: '/analysis/occupancy',
    available: false,
    icon: '▣',
    stat: '—',
    statLabel: 'avg occupancy',
  },
];

function berlinToday(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function hitColour(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return 'rgba(255,255,255,0.2)';
  const pct = rate * 100;
  if (pct >= 90) return '#4ade80';
  if (pct >= 70) return '#f0a500';
  return '#f87171';
}

const AnalysisLanding: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const today = berlinToday();

  // Accuracy now arrives through the SAME listener the /home page already uses:
  // the hourly rollup merges an `accuracy` field into aggregates/home_live, so
  // this page needs no fetch, no key, and no second listener.
  //
  // Note what this deliberately does NOT cover. The rollup precomputes two fixed
  // days at overall and per-line grain. /analysis/messages lets the user pick any
  // date and any journey, so MessagesSidebar and JourneyView still call the API
  // directly — serving those from Firestore would mean a document per day per
  // journey, which is a mirror of the database rather than a rollup.
  const home = useHomeLive();
  const accuracyBlock = home.doc?.accuracy ?? null;
  const accuracyDay = accuracyBlock?.today ?? null;
  const accuracyUpdatedMs = accuracyBlock?.updated_at?.toDate?.().getTime() ?? null;
  // Its own threshold: this block has an hourly writer, so the page-level
  // 3-minute staleness rule would mark a healthy figure stale immediately.
  const accuracyStale = accuracyUpdatedMs === null
    || Date.now() - accuracyUpdatedMs > ACCURACY_STALE_AFTER_MS;

  const accuracyError = home.error
    ? home.error.message
    : (home.loaded && !accuracyBlock ? 'Noch keine Genauigkeitsdaten in Firestore' : null);

  const byLine = toAccuracyByLine(accuracyDay?.by_line);
  const history = useAccuracyHistory(today);

  useEffect(() => {
    // Stagger mount for animation
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#0d1b2a',
      fontFamily: '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      backgroundImage: `
        linear-gradient(rgba(240,165,0,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(240,165,0,0.03) 1px, transparent 1px)
      `,
      backgroundSize: '48px 48px',
    }}>

      {/* Import IBM Plex Sans */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <Topbar menuState={false} openAction={() => {}} />

      {/* Main content */}
      <Box sx={{ pt: '64px', px: { xs: 3, md: 8 }, pb: 8 }}>

        {/* Header */}
        <Box sx={{
          pt: 8, pb: 6,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}>
          <Typography sx={{
            fontFamily: '"IBM Plex Mono"',
            fontSize: '11px',
            letterSpacing: '0.2em',
            color: '#f0a500',
            textTransform: 'uppercase',
            mb: 2,
          }}>
            Stuttgart S-Bahn · Datenanalyse
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: { xs: '28px', md: '40px' },
            fontWeight: 300,
            color: '#ffffff',
            lineHeight: 1.15,
            mb: 2,
          }}>
            Analyse &{' '}
            <span style={{ color: '#f0a500', fontWeight: 500 }}>Qualitätssicherung</span>
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: '15px',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.45)',
            maxWidth: '540px',
            lineHeight: 1.7,
          }}>
            Diagnostic tools for exploring forecast message coverage, delay patterns,
            and occupancy data quality across the Stuttgart S-Bahn network.
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{
          height: '1px',
          background: 'linear-gradient(90deg, #f0a500 0%, rgba(240,165,0,0.1) 40%, transparent 100%)',
          mb: 6,
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease 0.2s',
        }} />

        {/* Forecast accuracy — today, per UIC */}
        <Box sx={{
          mb: 6,
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '6px',
          padding: '24px 32px',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s',
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 340px) 1fr' },
            gap: 4,
            alignItems: 'start',
          }}>
            {/* Day overall */}
            <Box>
              <AccuracyBox
                scope="overall"
                day={today}
                node={accuracyDay ? toAccuracyNode(accuracyDay.overall) : (accuracyError ? {} : undefined)}
                title={`Prognosegüte heute (${today})`}
              />
              {/* A partial day, and it must say so. n_samples is what separates
                  a morning figure over a few thousand samples from an evening
                  one over a hundred thousand — the rate alone hides that. */}
              {accuracyDay && (
                <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', fontFamily: '"IBM Plex Mono"', mt: '4px' }}>
                  Teiltag · {(accuracyDay.n_samples ?? 0).toLocaleString('de-DE')} Stichproben
                  {accuracyDay.model_source && accuracyDay.model_source !== 'forecaster_archive'
                    && ' · Modellklassen nicht verfügbar'}
                  {(accuracyDay.journey_fetch_errors ?? 0) > 0
                    && ` · ${accuracyDay.journey_fetch_errors} Fahrten nicht abrufbar`}
                  {accuracyStale && ' · veraltet'}
                </Typography>
              )}
              {accuracyError && (
                <Typography sx={{ fontSize: '10px', color: '#f87171', fontFamily: '"IBM Plex Mono"', mt: '4px' }}>
                  {accuracyError}
                </Typography>
              )}
            </Box>

            {/* Per-line mini table */}
            {Object.keys(byLine).length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{
                  borderCollapse: 'collapse', width: '100%',
                  '& th, & td': {
                    fontFamily: '"IBM Plex Mono"', fontSize: '11px',
                    padding: '3px 10px', textAlign: 'right', whiteSpace: 'nowrap',
                  },
                  '& th': {
                    color: 'rgba(255,255,255,0.35)', fontWeight: 500,
                    letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '9px',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  },
                }}>
                  <thead>
                    <tr>
                      <Box component="th" sx={{ textAlign: 'left !important' }}>Linie</Box>
                      {HORIZON_KEYS.map(h => <th key={h}>{h}</th>)}
                      <th>Alle</th>
                      <th>n</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(byLine).map(([line, node]) => (
                      <tr key={line}>
                        <Box component="td" sx={{ textAlign: 'left !important', color: '#f0a500', fontWeight: 600 }}>
                          {line}
                        </Box>
                        {HORIZON_KEYS.map(h => (
                          <Box component="td" key={h} sx={{ color: hitColour(node[h]?.class_hit_rate) }}>
                            {node[h]?.class_hit_rate != null ? `${(node[h].class_hit_rate! * 100).toFixed(0)}%` : '—'}
                          </Box>
                        ))}
                        <Box component="td" sx={{ color: hitColour(node.all?.class_hit_rate), fontWeight: 600 }}>
                          {pctLabel(node.all?.class_hit_rate)}
                        </Box>
                        <Box component="td" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                          {node.all?.n.toLocaleString() ?? '—'}
                        </Box>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}
          </Box>

          {/* Hourly series. Differenced from the cumulative snapshots — see
              useAccuracyHistory. Empty until the second hourly run. */}
          <Box sx={{ mt: 4 }}>
            <AccuracyChart
              points={history.points}
              day={today}
              loading={!history.loaded}
              error={history.error}
            />
          </Box>
        </Box>

        {/* Cards grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}>
          {CARDS.map((card, i) => (
            <Box
              key={card.id}
              onClick={() => card.available && navigate(card.route)}
              sx={{
                position: 'relative',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '6px',
                padding: '32px',
                cursor: card.available ? 'pointer' : 'default',
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.5s ease ${0.15 + i * 0.08}s, transform 0.5s ease ${0.15 + i * 0.08}s, border-color 0.2s ease, background-color 0.2s ease`,
                '&:hover': card.available ? {
                  borderColor: 'rgba(240,165,0,0.5)',
                  backgroundColor: 'rgba(240,165,0,0.04)',
                } : {},
                overflow: 'hidden',
              }}
            >
              {/* Coming soon overlay */}
              {!card.available && (
                <Box sx={{
                  position: 'absolute',
                  top: 16, right: 16,
                  backgroundColor: 'rgba(240,165,0,0.1)',
                  border: '1px solid rgba(240,165,0,0.25)',
                  borderRadius: '3px',
                  padding: '2px 8px',
                }}>
                  <Typography sx={{ fontSize: '10px', color: '#f0a500', fontFamily: '"IBM Plex Mono"', letterSpacing: '0.1em' }}>
                    IN VORBEREITUNG
                  </Typography>
                </Box>
              )}

              {/* Icon */}
              <Typography sx={{
                fontSize: '28px',
                color: 'rgba(240,165,0,0.6)',
                mb: 2,
                fontFamily: '"IBM Plex Mono"',
                lineHeight: 1,
              }}>
                {card.icon}
              </Typography>

              {/* Title */}
              <Typography sx={{
                fontFamily: '"IBM Plex Sans"',
                fontSize: '18px',
                fontWeight: 500,
                color: '#ffffff',
                mb: 0.5,
              }}>
                {card.title}
              </Typography>
              <Typography sx={{
                fontFamily: '"IBM Plex Mono"',
                fontSize: '11px',
                color: 'rgba(240,165,0,0.7)',
                letterSpacing: '0.1em',
                mb: 2,
              }}>
                {card.subtitle}
              </Typography>

              {/* Description */}
              <Typography sx={{
                fontFamily: '"IBM Plex Sans"',
                fontSize: '13px',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.45)',
                lineHeight: 1.7,
                mb: 3,
              }}>
                {card.description}
              </Typography>

              {/* Stat */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{
                  fontFamily: '"IBM Plex Mono"',
                  fontSize: '24px',
                  fontWeight: 500,
                  color: card.available ? '#f0a500' : 'rgba(255,255,255,0.15)',
                }}>
                  {card.stat}
                </Typography>
                <Typography sx={{
                  fontFamily: '"IBM Plex Sans"',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.25)',
                }}>
                  {card.statLabel}
                </Typography>
              </Box>

              {/* Bottom accent line */}
              <Box sx={{
                position: 'absolute',
                bottom: 0, left: 0, right: 0,
                height: '2px',
                background: card.available
                  ? 'linear-gradient(90deg, #f0a500, transparent)'
                  : 'linear-gradient(90deg, rgba(255,255,255,0.05), transparent)',
              }} />
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{
          mt: 8, pt: 4,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease 0.6s',
        }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
            STUTTGART S-BAHN · OCCUPANCY INTELLIGENCE
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
            {BUILD_VERSION}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalysisLanding;